import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  endpointsService, 
  EndpointCreateData, 
  EndpointUpdateData,
  EndpointTestRequest,
  Endpoint
} from '@/services/endpoints.service';
import { toast } from 'sonner';

// Helper function for error handling
function getErrorMessage(error: unknown, defaultMessage: string): string {
  if (error && 
      typeof error === 'object' && 
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object' &&
      'message' in error.response.data &&
      typeof error.response.data.message === 'string') {
    return error.response.data.message;
  }
  return defaultMessage;
}

// Query Keys
export const endpointKeys = {
  all: ['endpoints'] as const,
  lists: () => [...endpointKeys.all, 'list'] as const,
  list: (productId: string) => [...endpointKeys.lists(), productId] as const,
  details: () => [...endpointKeys.all, 'detail'] as const,
  detail: (productId: string, endpointId: string) => [...endpointKeys.details(), productId, endpointId] as const,
};

export function useEndpoints(productId: string) {
  return useQuery({
    queryKey: endpointKeys.list(productId),
    queryFn: () => endpointsService.getEndpoints(productId),
    enabled: !!productId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useEndpoint(productId: string, endpointId: string) {
  return useQuery({
    queryKey: endpointKeys.detail(productId, endpointId),
    queryFn: () => endpointsService.getEndpoint(productId, endpointId),
    enabled: !!productId && !!endpointId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useCreateEndpoint() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: EndpointCreateData }) =>
      endpointsService.createEndpoint(productId, data),
    onSuccess: (newEndpoint, { productId }) => {
      // Invalidate endpoints list
      queryClient.invalidateQueries({ queryKey: endpointKeys.list(productId) });
      
      // Optimistically add to cache
      queryClient.setQueryData(endpointKeys.detail(productId, newEndpoint.id), newEndpoint);
      
      toast.success('Endpoint created successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to create endpoint'));
    },
  });
}

export function useUpdateEndpoint() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, endpointId, data }: { productId: string; endpointId: string; data: EndpointUpdateData }) =>
      endpointsService.updateEndpoint(productId, endpointId, data),
    onSuccess: (updatedEndpoint, { productId, endpointId }) => {
      // Update the specific endpoint in cache
      queryClient.setQueryData(endpointKeys.detail(productId, endpointId), updatedEndpoint);
      
      // Invalidate endpoints list to refresh summaries
      queryClient.invalidateQueries({ queryKey: endpointKeys.list(productId) });
      
      toast.success('Endpoint updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update endpoint'));
    },
  });
}

export function useDeleteEndpoint() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, endpointId }: { productId: string; endpointId: string }) =>
      endpointsService.deleteEndpoint(productId, endpointId),
    onSuccess: (_, { productId, endpointId }) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: endpointKeys.detail(productId, endpointId) });
      
      // Invalidate list to refresh
      queryClient.invalidateQueries({ queryKey: endpointKeys.list(productId) });
      
      toast.success('Endpoint deleted successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to delete endpoint'));
    },
  });
}

export function useSyncToApisix() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productId: string) => endpointsService.syncToApisix(productId),
    onSuccess: (result, productId) => {
      // Invalidate endpoints list to refresh sync status
      queryClient.invalidateQueries({ queryKey: endpointKeys.list(productId) });

      const extractCount = (payload: unknown, key: 'synced' | 'failed'): number => {
        if (!payload || typeof payload !== 'object') return 0;

        const record = payload as Record<string, unknown>;
        const snake = key === 'synced' ? 'synced_count' : 'failed_count';
        const camel = key === 'synced' ? 'syncedCount' : 'failedCount';

        const direct = record[snake] ?? record[camel];
        if (typeof direct === 'number') return direct;

        const meta = record.meta;
        if (meta && typeof meta === 'object') {
          const metaRecord = meta as Record<string, unknown>;
          const metaSnake = metaRecord[snake];
          const metaCamel = metaRecord[camel];
          const metaFlat = key === 'synced' ? metaRecord.synced : metaRecord.failed;
          if (typeof metaSnake === 'number') return metaSnake;
          if (typeof metaCamel === 'number') return metaCamel;
          if (typeof metaFlat === 'number') return metaFlat;
        }

        const data = record.data;
        if (data && typeof data === 'object') {
          const dataRecord = data as Record<string, unknown>;
          const dataDirect = dataRecord[snake] ?? dataRecord[camel];
          const dataFlat = key === 'synced' ? dataRecord.synced : dataRecord.failed;
          if (typeof dataDirect === 'number') return dataDirect;
          if (typeof dataFlat === 'number') return dataFlat;
        }

        const results =
          Array.isArray(record.results) ? record.results :
          (data && typeof data === 'object' && Array.isArray((data as Record<string, unknown>).results))
            ? (data as Record<string, unknown>).results as Array<Record<string, unknown>>
            : null;

        if (results) {
          return results.filter((item) => item?.status === (key === 'synced' ? 'success' : 'error')).length;
        }

        return 0;
      };

      const syncedCount = extractCount(result, 'synced');
      const failedCount = extractCount(result, 'failed');

      toast.success(`Synced ${syncedCount} endpoint${syncedCount === 1 ? '' : 's'} successfully`);

      if (failedCount > 0) {
        toast.error(`${failedCount} endpoint${failedCount === 1 ? '' : 's'} failed to sync`);
      }
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to sync endpoints'));
    },
  });
}

export function useSyncEndpoint() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, endpointId }: { productId: string; endpointId: string }) =>
      endpointsService.syncEndpoint(productId, endpointId),
    onSuccess: (result, { productId, endpointId }) => {
      updateEndpointInCache(queryClient, productId, endpointId, (endpoint) => {
        const timestamp = new Date().toISOString();
        return {
          ...endpoint,
          apisix_sync_status: 'synced',
          apisixSyncStatus: 'synced',
          sync_status: 'synced',
          last_synced_at: timestamp,
          lastSyncedAt: timestamp,
        };
      });

      const syncedCount = typeof (result as { synced?: number }).synced === 'number'
        ? (result as { synced?: number }).synced
        : extractCount(result, 'synced');

      toast.success(
        `Synced ${syncedCount || 1} endpoint${(syncedCount || 1) === 1 ? '' : 's'} successfully`
      );
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to sync endpoint'));
    },
  });
}

export function useRemoveEndpointFromGateway() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, endpointId }: { productId: string; endpointId: string }) =>
      endpointsService.removeEndpointFromGateway(productId, endpointId),
    onSuccess: (_, { productId, endpointId }) => {
      updateEndpointInCache(queryClient, productId, endpointId, (endpoint) => ({
        ...endpoint,
        apisix_sync_status: 'pending',
        apisixSyncStatus: 'pending',
        sync_status: 'pending',
        apisix_route_id: null,
        apisix_service_id: null,
        last_synced_at: null,
        lastSyncedAt: null,
      }));
      toast.success('Removed endpoint from gateway');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to remove endpoint from gateway'));
    },
  });
}

export function useTestEndpoint() {
  return useMutation({
    mutationFn: ({ productId, endpointId, testData }: { productId: string; endpointId: string; testData: EndpointTestRequest }) =>
      endpointsService.testEndpoint(productId, endpointId, testData),
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to test endpoint'));
    },
  });
}

// Prefetch Endpoint Hook (for hover/preview)
export function usePrefetchEndpoint() {
  const queryClient = useQueryClient();

  return (productId: string, endpointId: string) => {
    queryClient.prefetchQuery({
      queryKey: endpointKeys.detail(productId, endpointId),
      queryFn: () => endpointsService.getEndpoint(productId, endpointId),
      staleTime: 5 * 60 * 1000,
    });
  };
}

// Shared count extractor for sync responses
function extractCount(payload: unknown, key: 'synced' | 'failed'): number {
  if (!payload || typeof payload !== 'object') return 0;

  const record = payload as Record<string, unknown>;
  const snake = key === 'synced' ? 'synced_count' : 'failed_count';
  const camel = key === 'synced' ? 'syncedCount' : 'failedCount';

  const direct = record[snake] ?? record[camel];
  if (typeof direct === 'number') return direct;

  const meta = record.meta;
  if (meta && typeof meta === 'object') {
    const metaRecord = meta as Record<string, unknown>;
    const metaSnake = metaRecord[snake];
    const metaCamel = metaRecord[camel];
    const metaFlat = key === 'synced' ? metaRecord.synced : metaRecord.failed;
    if (typeof metaSnake === 'number') return metaSnake;
    if (typeof metaCamel === 'number') return metaCamel;
    if (typeof metaFlat === 'number') return metaFlat;
  }

  const data = record.data;
  if (data && typeof data === 'object') {
    const dataRecord = data as Record<string, unknown>;
    const dataDirect = dataRecord[snake] ?? dataRecord[camel];
    const dataFlat = key === 'synced' ? dataRecord.synced : dataRecord.failed;
    if (typeof dataDirect === 'number') return dataDirect;
    if (typeof dataFlat === 'number') return dataFlat;
  }

  const results =
    Array.isArray(record.results) ? record.results :
    (data && typeof data === 'object' && Array.isArray((data as Record<string, unknown>).results))
      ? (data as Record<string, unknown>).results as Array<Record<string, unknown>>
      : null;

  if (results) {
    return results.filter((item) => item?.status === (key === 'synced' ? 'success' : 'error')).length;
  }

  return 0;
}

function updateEndpointInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  productId: string,
  endpointId: string,
  updater: (endpoint: Endpoint) => Endpoint
) {
  queryClient.setQueryData(endpointKeys.list(productId), (existing) => {
    if (!existing || typeof existing !== 'object') return existing;
    const current = existing as { data?: Endpoint[]; meta?: unknown };
    if (!Array.isArray(current.data)) return existing;

    return {
      ...current,
      data: current.data.map((endpoint) =>
        endpoint.id === endpointId ? updater(endpoint) : endpoint
      ),
    };
  });

  queryClient.setQueryData(endpointKeys.detail(productId, endpointId), (existing) => {
    if (!existing || typeof existing !== 'object') return existing;
    return updater(existing as Endpoint);
  });
}
